import React, { useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { useTranslation } from '../../context/translationContext';
import useSessionStorage from '../../hooks/useSessionStorage';

interface Props {
  data: any[];
  title?: string;
  icon?: JSX.Element;
  onSubmit?: (e: any) => void;
  defaultSelectedItems?: string[];
  colId: string;
  translateEnum?: any;
}

/**
 * Select component able to select multiple items
 */
export default function SelectMultiple({
  data,
  title,
  icon,
  onSubmit,
  defaultSelectedItems,
  colId,
  translateEnum,
}: Props) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<any>(defaultSelectedItems ?? []);
  const [selectedItemsStorage, setSelectedItemsStorage] = useSessionStorage<string[]>(
    'table_col_filter_' + colId,
    [],
  );

  // Sessionstorage listener
  useEffect(() => {
    // reset filters
    const handleStorageChange = () => {
      if (window.sessionStorage.getItem('table_col_filter_' + colId) === null) {
        setSelectedItems([]);
      }
    };
    // Add the event listener when the component mounts
    window.addEventListener('storage', handleStorageChange);
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    setSelectedItems(selectedItemsStorage);
  }, []);

  useEffect(() => {
    setSelectedItemsStorage(selectedItems);
  }, [selectedItems]);

  /**
   * Checks if item is selected
   */
  const isSelected = (value: string) => {
    return selectedItems.find((el: any) => el === value) === undefined ? false : true;
  };

  /**
   * Selects item
   */
  const handleSelect = (value: any) => {
    if (!isSelected(value)) {
      const selectedItemsUpdated = [...selectedItems, data.find((el) => el === value)];
      setSelectedItems(selectedItemsUpdated);
    } else {
      handleDeselect(value);
    }
    setIsOpen(true);
  };

  /**
   * Removes selection
   */
  const handleDeselect = (value: any) => {
    const selectedItemsUpdated = selectedItems.filter((el: any) => el !== value);
    setSelectedItems(selectedItemsUpdated);
    setIsOpen(true);
  };

  /**
   * Submit when filter value changes
   */
  useEffect(() => {
    onSubmit && onSubmit(selectedItems);
  }, [selectedItems]);
  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-xs mx-auto">
        <Listbox
          as="div"
          className="space-y-1"
          value={selectedItems}
          onChange={(value: any) => handleSelect(value)}
        >
          {() => (
            <>
              {title && (
                <Listbox.Label className="block text-sm leading-5 font-medium text-gray-700">
                  {title}
                </Listbox.Label>
              )}
              <div className="">
                <span className="">
                  {selectedItems.length < 1 ? (
                    <span onClick={() => setIsOpen(!isOpen)}>{icon ?? 'Select items'}</span>
                  ) : (
                    <Listbox.Button
                      className="hover:pointer ml-3"
                      onClick={() => setIsOpen(!isOpen)}
                    >
                      {`(${selectedItems.length})`}
                    </Listbox.Button>
                  )}
                </span>

                <Transition
                  show={isOpen}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  className="absolute mt-2 max-h-96 -translate-x-20 overflow-auto w-40 rounded-md bg-white shadow-lg text-center min-w-fit"
                  onMouseLeave={() => setIsOpen(false)}
                >
                  <Listbox.Options
                    static
                    className="rounded-md py-1 text-base shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5 text-left"
                  >
                    {data.map((item) => {
                      const selected = isSelected(item);
                      return (
                        <Listbox.Option key={item} value={item}>
                          {({ active }) => (
                            <div
                              className={`${
                                active ? 'text-white bg-blue-600' : 'text-gray-900'
                              } cursor-default select-none relative py-2 pl-8 pr-4`}
                            >
                              <span
                                className={`${
                                  selected ? 'font-semibold' : 'font-normal'
                                } block truncate`}
                              >
                                <input type={'checkbox'} checked={selected} readOnly />{' '}
                                {translateEnum
                                  ? t(translateEnum[item])
                                  : item === null
                                  ? t('general.no-name')
                                  : item}
                              </span>
                            </div>
                          )}
                        </Listbox.Option>
                      );
                    })}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>
      </div>
    </div>
  );
}
